import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import './index.css';
import App from './App.jsx';
import { AuthProvider } from './context/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';
import axios from 'axios';

// Import your route components
import Login from './components/Login';
import Signup from './components/Signup';
import OnboardingFlow from './pages/Onboarding/OnboardingFlow';
import TestInterface from './pages/TestInterface';
import NotFound from './components/NotFound';
import FirestoreRetryPage from './components/FirestoreRetryPage';

// Error Boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>Something went wrong.</h1>
          <p>Please refresh the page or try again later.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Check if we're on a page that needs React
const rootElement = document.getElementById('root');

// If we're on the landing page or payment page, don't try to mount React
const isLandingPage = window.location.pathname === '/' ||
                     window.location.pathname === '/landing.html' ||
                     window.location.pathname === '/payment.html';

// Only proceed with React mounting if we're not on the landing page and the root element exists
if (!rootElement && !isLandingPage) {
  console.warn('Root element not found, but we appear to be on a React page. This may cause issues.');
  // Don't throw an error, just exit gracefully
  // This allows the landing page to load without errors
  if (window.location.pathname !== '/signup') {
    // If we're not on the signup page, we can exit
    console.log('Not on a React page, skipping React initialization');
    // Still allow the rest of the script to run for interceptors
  }
}

// Only create the root if the element exists
const root = rootElement ? ReactDOM.createRoot(rootElement) : null;

// Initial render with debugging
console.log('Rendering application...');

// Add this before the ReactDOM.render call
axios.interceptors.request.use(
  config => {
    console.log('AXIOS REQUEST INTERCEPTED:', {
      url: config.url,
      method: config.method,
      data: config.data,
      timestamp: new Date().toISOString()
    });
    return config;
  },
  error => {
    console.error('AXIOS REQUEST ERROR:', error);
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  response => {
    console.log('AXIOS RESPONSE INTERCEPTED:', {
      url: response.config.url,
      method: response.config.method,
      status: response.status,
      timestamp: new Date().toISOString()
    });
    return response;
  },
  error => {
    console.error('AXIOS RESPONSE ERROR:', error);
    return Promise.reject(error);
  }
);

const originalFetch = window.fetch;
window.fetch = function(...args) {
  console.log('FETCH INTERCEPTED:', {
    url: args[0],
    options: args[1],
    timestamp: new Date().toISOString()
  });
  return originalFetch.apply(this, args);
};

const originalXHR = window.XMLHttpRequest;
window.XMLHttpRequest = function() {
  const xhr = new originalXHR();
  const originalOpen = xhr.open;
  xhr.open = function(...args) {
    console.log('XHR INTERCEPTED:', {
      url: args[1],
      method: args[0],
      timestamp: new Date().toISOString()
    });
    return originalOpen.apply(xhr, args);
  };
  return xhr;
};

// Create a router with your routes
const router = createBrowserRouter([
  {
    path: "*",
    element: <App />,
    errorElement: <NotFound />
    // We don't need to define children here, since App.jsx has its own <Routes> component
    // that already handles these routes
  }
]);

// Only render if we have a root
if (root) {
  root.render(
    <ErrorBoundary>
      <ConfigProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ConfigProvider>
    </ErrorBoundary>
  );
} else {
  console.log('React not initialized - either on landing page or root element not found');
}

// Enhanced error handling
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);

  // Handle Firestore offline errors specifically
  if (event.reason && (
      event.reason.code === 'unavailable' ||
      event.reason.code === 'failed-precondition' ||
      event.reason.message === 'Failed to get document because the client is offline.'
    )) {
    console.log('Firestore connection issue detected. Attempting to work in offline mode.');
    // Don't show alert to avoid disrupting user experience
    event.preventDefault();
    return;
  }

  // For Firebase auth errors, handle silently
  if (event.reason && event.reason.code && event.reason.code.startsWith('auth/')) {
    console.error('Firebase auth error:', event.reason);
    event.preventDefault();
    return;
  }

  // For other critical errors, log but don't alert (alerts can be disruptive)
  console.error('Critical application error:', event.reason);
  event.preventDefault();
});

console.log('Application initialized');
